import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';

// Components
import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import FeaturedTrucks from '../FeaturedTrucks';
import CtaImage from '../content/CtaImage';
import LogoList from '../content/LogoList';
import AnyEvent from '../content/AnyEvent';
import WhyBookWithUs from '../content/WhyBookWithUs';
import FeaturedCuisines from '../FeaturedCuisines';
import HowItWorks from '../content/HowItWorks';
import DessertFaq from '../faqs/DessertFaq';
import CateringCta from '../content/CateringCta';
import TripleCta from '../content/TripleCta';
import Testimonials from '../content/Testimonials';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';

function DessertCatering() {
  const { vehicle_type, city_name, site_title } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();

  const featuredTrucks = { food_categories: 'dessert' };

  return (
    <div className='CateringPage'>
      <Meta
        title={`Dessert Food ${vehicle_type} Catering`}
        type='website'
        canonical='/food-truck-catering/dessert-catering'
        description={`Book dessert food ${vehicleType} catering in ${city_name} for weddings, parties, and events. Ice cream, donuts, cupcakes & more. Get free quotes from top dessert ${vehicleType}s.`}
        image='https://static.seattlefoodtruck.com/photos/dessert1.webp'
        structuredData={{
          cateringService: {
            name: `Dessert Food ${vehicle_type} Catering in ${city_name}`,
            description: `Book dessert food ${vehicleType} catering in ${city_name} for weddings, parties, and events.`,
            url: '/food-truck-catering/dessert-catering',
            serviceType: 'Dessert Food Truck Catering',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' },
              { '@type': 'ListItem', position: 3, name: 'Dessert Catering', item: '/food-truck-catering/dessert-catering' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/dessert1.webp' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>Dessert Food {vehicle_type} Catering for Your Event</h1>
            <p>Make your event extra sweet with a dessert food {vehicleType}! Whether you&#39;re hosting a <Link to='/food-truck-catering/wedding-catering'>wedding</Link>, <Link to='/food-truck-catering/corporate-catering'>corporate event</Link>, <Link to='/food-truck-catering/birthday-parties'>birthday party</Link>, or <Link to='/food-truck-catering/community-events'>festival</Link>, our network of top-rated dessert {vehicleType}s will serve up delicious treats that your guests will love. From gourmet ice cream and handcrafted donuts to decadent cupcakes and artisanal pastries, {site_title} makes it effortless to find and book the best dessert food {vehicleType}s in town. Simply tell us about your event, receive tailored quotes, and secure your perfect match — it&#39;s that easy!</p>
            <p>
              <Link to='/catering/register' className='Button Button--mobile-w100'>Start Your Dessert Catering Request</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList title='Trusted by leading companies' titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <TripleCta
        cuisine='dessert'
        image='https://static.seattlefoodtruck.com/photos/dessert2.webp'
        optionsText='Options include gourmet ice cream, boba tea, churros, cupcakes, and more.'
      />

      <FeaturedTrucks title={`Featured Dessert Food ${vehicle_type}s`} options={featuredTrucks} />

      <WhyBookWithUs title={`Why Book a Dessert Food ${vehicle_type} for Catering?`} />

      <section className='Section--extraPadding'>
        <h2 className='u-textCenter u-mb3'>What Makes Us Different?</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/dessert3.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Endless Sweet Options</h4>
            <p>
              {site_title} partners with the best dessert food {vehicleType}s in the city, offering a variety of delicious treats. Whether you&#39;re looking for classic ice cream, rich chocolate brownies, gourmet cupcakes, or creative fusion desserts, our vendors use fresh, high-quality ingredients to ensure a memorable experience. Many of our {vehicleType}s offer seasonal flavors and custom dessert bars, allowing you to tailor the menu to your event&#39;s theme.
            </p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={styles.Cta}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/dessert4.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Effortless Dessert Catering for Any Event</h4>
            <p>
              Booking a dessert food {vehicleType} should be as easy as enjoying your favorite treat. Our seamless process allows you to enter your event details, receive multiple quotes from verified dessert vendors, and book in minutes. Whether you need a unique late-night wedding snack or a fun dessert option for a corporate event, our platform ensures a hassle-free experience.
            </p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/dessert5.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Customizable Menus & Dietary Options</h4>
            <p>
              Need vegan or gluten-free dessert options? Want a dessert bar with interactive stations? Our dessert food {vehicleType}s can customize their menus to fit your guests&#39; dietary preferences. Whether you want an elegant selection of pastries or a fun, hands-on experience with made-to-order treats, we&#39;ve got you covered.
            </p>
          </div>
        </div>
      </section>

      <HowItWorks />

      <AnyEvent title={`Dessert Food ${vehicle_type} Catering for Any Event`} />

      <Testimonials title="Join Thousands Who've Booked Dessert Catering With Us!" />

      <CostEstimator title='Dessert Catering Cost Estimator' />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <DessertFaq />
      </section>

      <section className='Section--extraPadding u-pt2'>
        <CateringCta />
      </section>
    </div>
  );
}

export default DessertCatering;
